import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleRounded } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import {
  EntryAssessmentPaper,
  EntryAssessmentQuestion,
  getEntryAssessment,
  submitEntryAssessment,
} from '../services/api';

type Lang = 'en' | 'hi';

const COPY: Record<Lang, Record<string, string>> = {
  en: {
    part: 'Part {n} of {t}',
    optional: 'Optional',
    placeholder: 'Write your question here…',
    answered: '{a} of {t} answered',
    left: 'Answer the {n} question(s) left to submit.',
    submit: 'Submit',
    submitting: 'Submitting…',
    thanks: 'Thank you!',
    thanksBody: 'Your answers help your coach get to know you.',
    start: 'Start talking to the coach',
    logout: 'Log out',
    retry: 'Try again',
  },
  hi: {
    part: 'भाग {n} / {t}',
    optional: 'वैकल्पिक',
    placeholder: 'अपना सवाल यहाँ लिखिए…',
    answered: '{t} में से {a} उत्तर दिए',
    left: 'जमा करने के लिए बाकी {n} सवालों के उत्तर दें।',
    submit: 'जमा करें',
    submitting: 'जमा हो रहा है…',
    thanks: 'धन्यवाद!',
    thanksBody: 'आपके उत्तरों से आपका कोच आपको बेहतर जान पाएगा।',
    start: 'कोच से बात शुरू करें',
    logout: 'लॉग आउट',
    retry: 'फिर से कोशिश करें',
  },
};

const fill = (s: string, vars: Record<string, string | number>) =>
  Object.entries(vars).reduce((out, [k, v]) => out.replace(`{${k}}`, String(v)), s);

// Answers survive a reload or a dropped connection on this device. A
// convenience only: the attempt itself is stored by the server on submit.
const draftKey = (userId?: number) => `entryAssessmentDraft_${userId ?? 'anon'}`;

const readDraft = (userId?: number): { paperId?: string; answers?: Record<string, string>; startedAt?: string } => {
  try {
    return JSON.parse(localStorage.getItem(draftKey(userId)) || '{}');
  } catch {
    return {};
  }
};

const writeDraft = (userId: number | undefined, value: object | null) => {
  try {
    if (value) localStorage.setItem(draftKey(userId), JSON.stringify(value));
    else localStorage.removeItem(draftKey(userId));
  } catch {
    /* storage unavailable: the draft is just not kept */
  }
};

/** The one-time curiosity assessment a student takes before their first chat.
 *  ProtectedRoute keeps them here until the backend reports it done. */
const EntryAssessmentPage: React.FC = () => {
  const { user, language, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [lang, setLang] = useState<Lang>(language);
  const [paper, setPaper] = useState<EntryAssessmentPaper | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [showMissing, setShowMissing] = useState(false);
  const startedAt = useRef<string>(new Date().toISOString());
  const t = COPY[lang];

  const goToCoach = useCallback(async () => {
    writeDraft(user?.id, null);
    try {
      await refreshUser();
    } finally {
      navigate('/chat', { replace: true });
    }
  }, [navigate, refreshUser, user?.id]);

  const load = useCallback(async (which: Lang) => {
    setLoading(true);
    setError(null);
    try {
      const state = await getEntryAssessment(which);
      if (state.status !== 'pending' || !state.paper) {
        await goToCoach();
        return;
      }
      setPaper(state.paper);
      const draft = readDraft(user?.id);
      if (draft.paperId === state.paper.paper_id) {
        setAnswers((current) => ({ ...(draft.answers || {}), ...current }));
        if (draft.startedAt) startedAt.current = draft.startedAt;
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [goToCoach, user?.id]);

  useEffect(() => {
    load(lang);
    // Reload only when the language changes; answers are keyed by id so they carry over.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    if (paper) writeDraft(user?.id, { paperId: paper.paper_id, answers, startedAt: startedAt.current });
  }, [answers, paper, user?.id]);

  const required = useMemo(
    () => (paper ? paper.sections.flatMap((s) => s.questions.filter((q) => q.required)) : []),
    [paper],
  );
  const missing = required.filter((q) => !(answers[q.id] || '').trim());
  const answeredCount = required.length - missing.length;
  const progress = required.length ? Math.round((answeredCount * 100) / required.length) : 0;

  const setAnswer = (id: string, value: string) => setAnswers((a) => ({ ...a, [id]: value }));

  const handleSubmit = async () => {
    if (!paper) return;
    if (missing.length) {
      setShowMissing(true);
      document.getElementById(`q-${missing[0].id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await submitEntryAssessment({
        paper_id: paper.paper_id,
        answers,
        language: lang,
        started_at: startedAt.current,
      });
      writeDraft(user?.id, null);
      setDone(true);
    } catch (e: any) {
      if (e.status === 409 && /already/i.test(e.message)) {
        await goToCoach();
        return;
      }
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-violet-50 to-slate-50 px-4">
        <div className="w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-sm">
          <CheckCircleRounded className="text-emerald-500" style={{ fontSize: 60 }} />
          <h1 className="mt-3 text-2xl font-bold text-slate-900">{t.thanks}</h1>
          <p className="mt-2 text-slate-600">{t.thanksBody}</p>
          <button
            onClick={goToCoach}
            className="mt-7 w-full rounded-xl bg-violet-600 py-3.5 font-semibold text-white hover:bg-violet-700"
          >
            {t.start}
          </button>
        </div>
      </div>
    );
  }

  const renderQuestion = (q: EntryAssessmentQuestion, number: number) => {
    const isMissing = showMissing && q.required && !(answers[q.id] || '').trim();
    return (
      <fieldset
        key={q.id}
        id={`q-${q.id}`}
        className={`scroll-mt-24 rounded-2xl border bg-white p-4 transition-colors ${
          isMissing ? 'border-rose-300 ring-2 ring-rose-100' : 'border-slate-200'
        }`}
      >
        <legend className="sr-only">{q.text}</legend>
        <p className="font-medium leading-relaxed text-slate-800">
          <span className="mr-1.5 font-semibold text-violet-600">{number}.</span>
          {q.text}
        </p>
        {q.hint && <p className="mt-1 text-sm text-slate-500">{q.hint}</p>}

        {q.type === 'scale' ? (
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {(q.options || []).map((o) => {
              const checked = answers[q.id] === o.id;
              return (
                <label
                  key={o.id}
                  className={`flex min-h-[48px] cursor-pointer items-center justify-center rounded-xl border px-2 py-2 text-center text-sm font-medium transition-colors ${
                    checked
                      ? 'border-violet-600 bg-violet-600 text-white'
                      : 'border-slate-200 text-slate-700 hover:border-violet-300 hover:bg-violet-50'
                  }`}
                >
                  <input
                    type="radio"
                    name={q.id}
                    value={o.id}
                    checked={checked}
                    onChange={() => setAnswer(q.id, o.id)}
                    className="sr-only"
                  />
                  {o.text}
                </label>
              );
            })}
          </div>
        ) : (
          <div className="mt-3">
            {!q.required && (
              <span className="mb-1.5 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                {t.optional}
              </span>
            )}
            <textarea
              value={answers[q.id] || ''}
              onChange={(e) => setAnswer(q.id, e.target.value.slice(0, q.max_length || 500))}
              placeholder={t.placeholder}
              rows={3}
              className="w-full resize-y rounded-xl border border-slate-300 p-3 text-slate-800 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
            />
            <p className="mt-1 text-right text-xs text-slate-400">
              {(answers[q.id] || '').length}/{q.max_length || 500}
            </p>
          </div>
        )}
      </fieldset>
    );
  };

  let counter = 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 to-slate-50">
      {/* Top bar: language, log out, progress */}
      <div className="sticky top-0 z-10 border-b border-slate-200/70 bg-white/90 backdrop-blur">
        <div className="mx-auto flex w-full max-w-2xl items-center justify-between gap-3 px-4 py-2.5">
          <div className="flex rounded-lg border border-slate-300 bg-white p-0.5 text-sm">
            {(['en', 'hi'] as Lang[]).map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                disabled={loading || submitting}
                className={`rounded-md px-3 py-1 ${lang === l ? 'bg-violet-600 text-white' : 'text-slate-600'}`}
              >
                {l === 'en' ? 'English' : 'हिंदी'}
              </button>
            ))}
          </div>
          <button onClick={logout} className="text-sm text-slate-500 hover:text-slate-700">
            {t.logout}
          </button>
        </div>
        {paper && (
          <div className="h-1 bg-violet-100">
            <div className="h-full bg-violet-600 transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>

      <div className="mx-auto w-full max-w-2xl px-4 pb-28 pt-6">
        {loading && !paper ? (
          <div className="flex justify-center py-20"><CircularProgress /></div>
        ) : error && !paper ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            {error}
            <button onClick={() => load(lang)} className="ml-3 font-semibold underline">{t.retry}</button>
          </div>
        ) : paper ? (
          <>
            <h1 className="text-2xl font-bold text-slate-900">{paper.title}</h1>
            <p className="mb-6 mt-2 leading-relaxed text-slate-600">{paper.instructions}</p>

            <div className="flex flex-col gap-8">
              {paper.sections.map((s, i) => (
                <section key={s.id}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-violet-600">
                    {fill(t.part, { n: i + 1, t: paper.sections.length })}
                  </p>
                  {s.instructions && <p className="mb-3 mt-1 font-medium text-slate-700">{s.instructions}</p>}
                  <div className="mt-2 flex flex-col gap-3">
                    {s.questions.map((q) => renderQuestion(q, ++counter))}
                  </div>
                </section>
              ))}
            </div>

            {error && <p className="mt-4 text-sm text-rose-600">{error}</p>}
          </>
        ) : null}
      </div>

      {paper && (
        <div className="fixed inset-x-0 bottom-0 border-t border-slate-200 bg-white/95 backdrop-blur">
          <div className="mx-auto flex w-full max-w-2xl items-center justify-between gap-3 px-4 py-3">
            <p className="text-sm text-slate-600">
              {fill(t.answered, { a: answeredCount, t: required.length })}
              {showMissing && missing.length > 0 && (
                <span className="block text-xs text-rose-600">{fill(t.left, { n: missing.length })}</span>
              )}
            </p>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className={`rounded-xl px-6 py-3 font-semibold text-white disabled:cursor-not-allowed ${
                missing.length ? 'bg-violet-300' : 'bg-violet-600 hover:bg-violet-700'
              }`}
            >
              {submitting ? t.submitting : t.submit}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EntryAssessmentPage;
